// Controller support — 1B: a virtual cursor so every mouse-driven panel (BUILD,
// Shop, quizzes, registry, claim mode…) is fully playable with a gamepad.
//
//   • RIGHT stick → moves an on-screen pointer (shows on use, auto-hides idle).
//   • A button    → if the pointer is over a clickable element, click it;
//                   otherwise tend the field (paw:click). One owner of A so it's
//                   never ambiguous with the in-world controls in heroController.
//   • B button    → Escape (closes modals / cancels placement).
//
// Mouse/keyboard are untouched. The cursor has pointer-events:none so
// elementFromPoint() sees the UI underneath it, and we synthesize real pointer/
// mouse events at its position so existing handlers (click + hover) just work.

import { emit } from './eventBus.js'
import { getBindings, isCapturing } from '../game/gamepadBindings.js'

const DEAD = 0.2
const SPEED = 820 // px/sec at full stick deflection
const IDLE_HIDE_MS = 4000
const CLICKABLE = 'button, a, input, select, textarea, label, [data-action], [role="button"]'

let cursorEl = null
let x = 0
let y = 0
let visible = false
let lastMoveAt = 0
let prevA = false
let prevB = false
let last = 0

export function mountGamepadCursor() {
  if (cursorEl || typeof document === 'undefined') return
  cursorEl = document.createElement('div')
  cursorEl.className = 'gp-cursor'
  cursorEl.setAttribute('aria-hidden', 'true')
  document.body.appendChild(cursorEl)
  x = window.innerWidth / 2
  y = window.innerHeight / 2
  last = performance.now()
  requestAnimationFrame(loop)
}

function loop(t) {
  requestAnimationFrame(loop)
  const dt = Math.min(0.05, (t - last) / 1000)
  last = t

  if (isCapturing()) { hide(); prevA = prevB = false; return } // setup wizard owns the pad
  const pads = navigator.getGamepads ? navigator.getGamepads() : []
  let gp = null
  for (const p of pads) { if (p && p.connected) { gp = p; break } }
  if (!gp) { hide(); return }

  const b = getBindings()
  // Pointer stick → move the pointer.
  const rx = (gp.axes[b.pointerX] ?? 0) * b.pointerXInv
  const ry = (gp.axes[b.pointerY] ?? 0) * b.pointerYInv
  if (Math.hypot(rx, ry) > DEAD) {
    x = clamp(x + rx * SPEED * dt, 0, window.innerWidth)
    y = clamp(y + ry * SPEED * dt, 0, window.innerHeight)
    show()
    lastMoveAt = t
    place()
    dispatchAt('pointermove')
    dispatchAt('mousemove')
  } else if (visible && t - lastMoveAt > IDLE_HIDE_MS) {
    hide()
  }

  // "Tend" button → click under the pointer, else paw.
  const aDown = !!gp.buttons[b.tend]?.pressed
  if (aDown && !prevA) {
    if (visible && overClickable()) clickAt()
    else emit('paw:click')
  }
  prevA = aDown

  // "Cancel" button → Escape (close modal / cancel placement).
  const bDown = !!gp.buttons[b.cancel]?.pressed
  if (bDown && !prevB) {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
  }
  prevB = bDown
}

function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v }
function place() { if (cursorEl) cursorEl.style.transform = `translate(${x}px, ${y}px)` }
function show() { if (!visible) { visible = true; cursorEl.classList.add('on') } }
function hide() { if (visible) { visible = false; cursorEl.classList.remove('on') } }

// Is the element under the pointer something the player can click?
function overClickable() {
  const el = document.elementFromPoint(x, y)
  if (!el) return false
  if (el.closest(CLICKABLE)) return true
  return getComputedStyle(el).cursor === 'pointer' // canvas tiles, custom controls
}

// Fire a realistic click sequence at the pointer so existing handlers respond.
function clickAt() {
  const el = document.elementFromPoint(x, y)
  if (!el) return
  for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']) {
    el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window, clientX: x, clientY: y }))
  }
}

// Drive hover-based UI (citizen-care tool, placement ghost, labels) as we move.
function dispatchAt(type) {
  const el = document.elementFromPoint(x, y)
  if (!el) return
  const Ctor = type.startsWith('pointer') ? PointerEvent : MouseEvent
  el.dispatchEvent(new Ctor(type, { bubbles: true, cancelable: true, clientX: x, clientY: y }))
}
